import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { translatorApi, fileUrl } from '../../api/client';
import { User, CheckCircle2, Lock } from 'lucide-react';
import StatusBadge from '../../components/dashboard/StatusBadge';
import DashModal from '../../components/dashboard/DashModal';
import DashToolbar from '../../components/dashboard/DashToolbar';
import TranslatorProfileView from '../../components/dashboard/TranslatorProfileView';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';
import PasswordChangeForm from '../../components/dashboard/PasswordChangeForm';
import '../../components/dashboard/AssignTranslatorForm.css';
import '../../components/dashboard/DashForm.css';

function MissionsTable({ rows }) {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  if (!rows.length) return <p className="dash-empty">{t.dashboard.empty}</p>;
  return (
    <div className="dash-table-wrap">
      <table className="dash-table">
        <thead>
          <tr>
            <th>{t.dashboard.client.colId}</th>
            <th>{t.dashboard.client.colDocument}</th>
            <th>{t.dashboard.client.colLangs}</th>
            <th>{tr.colClient}</th>
            <th>{t.dashboard.client.colStatus}</th>
            <th>{tr.colDeadline}</th>
            <th>{t.dashboard.actions}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id}>
              <td>{m.id}</td>
              <td>{m.document}</td>
              <td>{m.langs}</td>
              <td>{m.client}</td>
              <td><StatusBadge status={m.status} labels={t.dashboard.status} /></td>
              <td>{m.deadline || '—'}</td>
              <td><Link className="dash-link" to={`/pro/missions/${m.id}`}>{t.dashboard.view}</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TranslatorHomePage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const [data, setData] = useState(null);
  const [howOpen, setHowOpen] = useState(false);

  useEffect(() => {
    translatorApi.overview().then(setData).catch(() => setData(null));
  }, []);

  const stats = data?.stats || { open: 0, progress: 0, done: 0, dueSoon: 0, overdue: 0 };
  const pairs = data?.profile?.translatorProfile?.langPairs || [];

  return (
    <>
      <header className="dash-page-head">
        <h1>{tr.homeTitle}</h1>
        <p>{tr.homeSubtitle}</p>
      </header>

      <div className="dash-kpi-grid">
        <div className="dash-kpi"><em>{tr.statOpen}</em><strong>{stats.open}</strong><small>{tr.kpiOpen}</small></div>
        <div className="dash-kpi"><em>{tr.statProgress}</em><strong>{stats.progress}</strong><small>{tr.kpiProgress}</small></div>
        <div className={`dash-kpi${stats.dueSoon ? ' dash-kpi--warn' : ''}`}><em>{tr.statDueSoon}</em><strong>{stats.dueSoon}</strong><small>{tr.kpiDueSoon}</small></div>
        <div className={`dash-kpi${stats.overdue ? ' dash-kpi--danger' : ''}`}><em>{tr.statOverdue}</em><strong>{stats.overdue}</strong><small>{tr.kpiOverdue}</small></div>
      </div>

      <div className="dash-split">
        <section className="dash-panel">
          <h2>{tr.priorityMissions}</h2>
          {(data?.priorityMissions || []).length ? (
            (data.priorityMissions || []).map((m) => (
              <div className="dash-queue-item" key={m.id}>
                <div>
                  <strong>{m.id} · {m.document}</strong>
                  <p>{m.client} · {m.deadline || '—'} · {m.langs}</p>
                </div>
                <Link className="dash-link" to={`/pro/missions/${m.id}`}>{d.view}</Link>
              </div>
            ))
          ) : (
            <p className="dash-empty">{d.empty}</p>
          )}
        </section>
        <section className="dash-panel">
          <h2>{d.langPairs}</h2>
          <p style={{ color: 'var(--gray-600)', marginTop: 0 }}>{d.translatorInfoPairs}</p>
          <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {pairs.length ? pairs.map((p) => (
              <span key={`${p.source}-${p.target}`} className="dash-badge">{p.source}→{p.target}</span>
            )) : <span className="dash-badge">—</span>}
          </div>
          <button type="button" className="dash-btn-sm" style={{ marginTop: 14 }} onClick={() => setHowOpen(true)}>
            {d.howItWorks}
          </button>
        </section>
      </div>

      <section className="dash-panel">
        <h2>{tr.recentMissions}</h2>
        <MissionsTable rows={data?.recentMissions || []} />
      </section>

      <DashModal open={howOpen} title={d.assignHowTitle} onClose={() => setHowOpen(false)}>
        <p style={{ marginTop: 0, color: 'var(--gray-700)', lineHeight: 1.65 }}>{d.assignHowText}</p>
      </DashModal>
    </>
  );
}

export function TranslatorMissionsPage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const [missions, setMissions] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    translatorApi.missions().then((res) => setMissions(res.missions || [])).catch(() => setMissions([]));
  }, []);

  const filtered = missions.filter((m) => {
    if (statusFilter !== 'all' && m.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [m.id, m.document, m.client, m.langs].some((x) => String(x || '').toLowerCase().includes(q));
  });
  const missionsPg = usePagination(filtered, 6);

  return (
    <>
      <header className="dash-page-head">
        <h1>{tr.missionsTitle}</h1>
        <p>{tr.missionsSubtitle}</p>
      </header>
      <DashToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={tr.searchMissions}
        filters={[
          { id: 'all', label: tr.filterAll },
          { id: 'assigned', label: d.status.assigned },
          { id: 'in_progress', label: d.status.in_progress },
          { id: 'ready', label: d.status.ready },
          { id: 'delivered', label: d.status.delivered },
        ]}
        activeFilter={statusFilter}
        onFilter={setStatusFilter}
      />
      <section className="dash-panel">
        <MissionsTable rows={missionsPg.pageItems} />
        <Pagination page={missionsPg.page} totalPages={missionsPg.totalPages} total={missionsPg.total} onChange={missionsPg.setPage} />
      </section>
    </>
  );
}

export function TranslatorMissionDetailPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const d = t.dashboard;
  const tr = d.translator;
  const [mission, setMission] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState('');

  const reload = () => translatorApi.mission(id).then((res) => setMission(res.mission)).catch(() => setMission(null));

  useEffect(() => {
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setStatus = async (status) => {
    setBusy(true);
    setMsg('');
    try {
      const res = await translatorApi.updateMissionStatus(id, status);
      setMission(res.mission);
      setMsg(d.saved);
    } catch {
      setMsg(d.empty);
    } finally {
      setBusy(false);
    }
  };

  const requestVerification = async () => {
    setBusy(true);
    setMsg('');
    try {
      const res = await translatorApi.requestVerification(id, verifyNotes);
      setMission(res.mission);
      setMsg(d.saved);
      setVerifyOpen(false);
      setVerifyNotes('');
    } catch (err) {
      setMsg(err.data?.error || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setBusy(true);
    try {
      const res = await translatorApi.uploadDocuments(id, files, 'final');
      setMission(res.mission);
      setMsg(d.saved);
    } catch {
      setMsg('Upload error');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  if (!mission) return <p className="dash-empty">{d.empty}</p>;

  return (
    <>
      <header className="dash-page-head">
        <Link to="/pro/missions" className="dash-link">{tr.missionsTitle}</Link>
        <h1>{d.missionDetail} — {mission.id}</h1>
        <p>{mission.document} · {mission.langs}</p>
      </header>

      <div className="dash-stats">
        <div className="dash-stat"><span>{tr.colClient}</span><strong style={{ fontSize: '1.1rem' }}>{mission.client}</strong></div>
        <div className="dash-stat"><span>{tr.colDeadline}</span><strong style={{ fontSize: '1.1rem' }}>{mission.deadline || '—'}</strong></div>
        <div className="dash-stat"><span>{d.client.colStatus}</span><strong style={{ fontSize: '1.1rem' }}><StatusBadge status={mission.status} labels={d.status} /></strong></div>
      </div>

      {mission.status === 'assigned' && (
        <section className="dash-panel">
          <h2>{tr.respondTitle}</h2>
          <p style={{ marginTop: 0, color: 'var(--gray-600)' }}>{tr.respondHint}</p>
          <div className="dash-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setMsg('');
                try {
                  const res = await translatorApi.respondMission(id, 'accepted');
                  setMission(res.mission);
                  setMsg(d.saved);
                } catch {
                  setMsg(d.empty);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {tr.acceptMission}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setMsg('');
                try {
                  const res = await translatorApi.respondMission(id, 'refused');
                  setMission(res.mission);
                  setMsg(d.saved);
                } catch {
                  setMsg(d.empty);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {tr.refuseMission}
            </button>
          </div>
          {msg && <p className="dash-toast">{msg}</p>}
        </section>
      )}

      {mission.status !== 'refused' && (
        <section className="dash-panel" style={{ marginTop: mission.status === 'assigned' ? 16 : 0 }}>
          <h2>{d.changeStatus}</h2>
          <div className="dash-actions">
            <button type="button" className="btn btn-outline" disabled={busy || mission.status === 'assigned'} onClick={() => setStatus('in_progress')}>{d.startMission}</button>
            <button type="button" className="btn btn-outline" disabled={busy || mission.status === 'assigned'} onClick={() => setStatus('ready')}>{d.markReady}</button>
            <button type="button" className="btn btn-primary" disabled={busy || mission.status === 'assigned'} onClick={() => setStatus('delivered')}>{d.markDelivered}</button>
          </div>
          {mission.status === 'ready' && !['verification_requested','lawyer_notary_in_progress','correction_requested','verification_validated','final_control','ready_to_deliver','delivered','completed'].includes(mission.orderStatus) && (
            <div className="dash-actions" style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setVerifyOpen(true)}>
                Demander une vérification
              </button>
            </div>
          )}
          {msg && mission.status !== 'assigned' && <p className="dash-toast">{msg}</p>}
        </section>
      )}

      <section className="dash-panel" style={{ marginTop: 16 }}>
        <h2>{tr.uploadFinal || d.upload}</h2>
        <input type="file" multiple disabled={busy} onChange={onUpload} />
        <ul className="dash-file-list" style={{ marginTop: 16 }}>
          {(mission.documents || []).map((f) => (
            <li key={f.id}>
              <span>{f.name} ({f.type})</span>
              {f.url && <a className="dash-link" href={fileUrl(f.url)} target="_blank" rel="noreferrer">↗</a>}
            </li>
          ))}
        </ul>
      </section>

      <DashModal
        open={verifyOpen}
        title="Demander une vérification"
        onClose={() => setVerifyOpen(false)}
      >
        <div className="dash-form" style={{ maxWidth: '100%' }}>
          <div className="dash-field">
            <label>Motif / précisions pour le vérificateur</label>
            <textarea
              rows={3}
              value={verifyNotes}
              onChange={(e) => setVerifyNotes(e.target.value)}
              placeholder="Points à vérifier, questions juridiques…"
            />
          </div>
          <div className="dash-actions">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={requestVerification}>
              Envoyer la demande
            </button>
            <button type="button" className="btn btn-outline" onClick={() => setVerifyOpen(false)}>
              Annuler
            </button>
          </div>
          {msg && <p className="dash-toast">{msg}</p>}
        </div>
      </DashModal>
    </>
  );
}

export function TranslatorDocumentsPage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const [documents, setDocuments] = useState([]);
  const docsPg = usePagination(documents, 6);

  useEffect(() => {
    translatorApi.documents().then((d) => setDocuments(d.documents || [])).catch(() => setDocuments([]));
  }, []);

  const kindMap = { source: tr.kindSource, draft: tr.kindDraft, final: tr.kindFinal };

  return (
    <>
      <header className="dash-page-head">
        <h1>{tr.docsTitle}</h1>
        <p>{tr.docsSubtitle}</p>
      </header>
      <section className="dash-panel">
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>{tr.colFile}</th>
                <th>{tr.colMission}</th>
                <th>{tr.colKind}</th>
              </tr>
            </thead>
            <tbody>
              {docsPg.pageItems.map((doc) => (
                <tr key={doc.id}>
                  <td>{doc.url ? <a className="dash-link" href={fileUrl(doc.url)} target="_blank" rel="noreferrer">{doc.name}</a> : doc.name}</td>
                  <td>{doc.mission}</td>
                  <td>{kindMap[doc.type] || doc.type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={docsPg.page} totalPages={docsPg.totalPages} total={docsPg.total} onChange={docsPg.setPage} />
      </section>
    </>
  );
}

export function TranslatorProfilePage() {
  const { t } = useLanguage();
  const d = t.dashboard;
  const tr = d.translator;
  const [user, setUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editFrameKey, setEditFrameKey] = useState(0);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [toast, setToast] = useState('');

  const reload = () => translatorApi.profile().then((res) => setUser(res.user)).catch(() => setUser(null));

  useEffect(() => {
    reload();
  }, []);

  // Le formulaire embarqué envoie 'translatorUpdated' après une mise à jour réussie :
  // on ferme le modal et on recharge le profil
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data === 'translatorUpdated') {
        setEditOpen(false);
        setEditFrameKey((k) => k + 1);
        setToast('Profil mis à jour avec succès.');
        setTimeout(() => setToast(''), 4000);
        reload();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  return (
    <>
      <header className="dash-page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1>{tr.profileTitle}</h1>
          <p>{tr.profileSubtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary" onClick={() => setEditOpen(true)}>
            <User size={15} /> Modifier mon profil
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setPwdOpen(true)}>
            <Lock size={15} /> {t.auth.changePassword || 'Changer le mot de passe'}
          </button>
        </div>
      </header>

      {user ? (
        <TranslatorProfileView user={user} />
      ) : (
        <p className="dash-empty">Chargement du profil…</p>
      )}

      <DashModal
        open={editOpen}
        wide
        fill
        keepMounted
        title="Modifier mon profil"
        subtitle="Même formulaire que la page « Devenir professionnel »"
        onClose={() => { setEditOpen(false); reload(); }}
      >
        <iframe
          key={editFrameKey}
          src="/devenir-traducteur?embed=1&self=1"
          title="Modifier mon profil"
          style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
        />
      </DashModal>

      <DashModal
        open={pwdOpen}
        title={t.auth.changePassword || 'Changer le mot de passe'}
        onClose={() => setPwdOpen(false)}
      >
        <PasswordChangeForm />
      </DashModal>

      {toast && (
        <div className="admin-toast" role="status">
          <CheckCircle2 size={18} />
          <span>{toast}</span>
        </div>
      )}
    </>
  );
}

export function TranslatorAvailabilityPage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const [form, setForm] = useState({ availability: 'available', availabilityStatus: 'disponible', acceptsUrgent: true });
  const [msg, setMsg] = useState('');

  useEffect(() => {
    translatorApi.profile().then((res) => {
      const p = res.user?.translatorProfile || {};
      setForm({
        availability: p.availability || 'available',
        availabilityStatus: p.availabilityStatus || 'disponible',
        acceptsUrgent: p.acceptsUrgent !== false,
      });
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      await translatorApi.updateProfile({ translatorProfile: form });
      setMsg(d.saved);
    } catch (err) {
      setMsg(err.data?.error || d.empty);
    }
  };

  return (
    <>
      <header className="dash-page-head"><h1>{tr.availabilityTitle || 'Disponibilité'}</h1><p>{tr.availabilitySubtitle || 'Gérez votre statut et disponibilité.'}</p></header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel" style={{ maxWidth: 520 }}>
        <form className="dash-form" onSubmit={save}>
          <div className="dash-field">
            <label>Statut</label>
            <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })}>
              {['available', 'unavailable', 'busy'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="dash-field">
            <label>Libellé</label>
            <input value={form.availabilityStatus} onChange={(e) => setForm({ ...form, availabilityStatus: e.target.value })} />
          </div>
          <label className="dash-check">
            <input type="checkbox" checked={form.acceptsUrgent} onChange={(e) => setForm({ ...form, acceptsUrgent: e.target.checked })} />
            {tr.acceptsUrgent || 'Accepte les missions urgentes'}
          </label>
          <button type="submit" className="btn btn-primary">{d.save}</button>
        </form>
      </section>
    </>
  );
}

export function TranslatorNotificationsPage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState('');
  const notifPg = usePagination(items, 6);

  const reload = () => translatorApi.notifications().then((res) => setItems(res.notifications || [])).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);

  const markAll = async () => {
    try {
      await translatorApi.readNotifications();
      setMsg(d.saved);
      reload();
    } catch {
      setMsg('Error');
    }
  };

  return (
    <>
      <header className="dash-page-head dash-page-head--split">
        <div>
          <h1>{tr.notificationsTitle || 'Notifications'}</h1>
          <p>{tr.notificationsSubtitle || 'Vos alertes.'}</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={markAll}>{tr.markAllRead || 'Tout marquer comme lu'}</button>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        {items.length === 0 ? <p className="dash-empty">{d.empty}</p> : (
          <ul className="dash-notif-list">
            {notifPg.pageItems.map((n) => (
              <li key={n.id} className={n.readAt ? 'dash-notif-read' : 'dash-notif-unread'}>
                <strong>{n.title}</strong><p>{n.body}</p><small>{n.createdAt ? new Date(n.createdAt).toLocaleString() : '—'}</small>
              </li>
            ))}
          </ul>
        )}
        <Pagination page={notifPg.page} totalPages={notifPg.totalPages} total={notifPg.total} onChange={notifPg.setPage} />
      </section>
    </>
  );
}

export function TranslatorMessagesPage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const [messages, setMessages] = useState([]);
  const [missions, setMissions] = useState([]);
  const [body, setBody] = useState('');
  const [toSel, setToSel] = useState('');
  const [threadKey, setThreadKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const contacts = useMemo(
    () => (missions || []).filter((m) => m.clientId).map((m) => ({ cid: m.clientId, oid: m.orderId, label: `${m.client || 'Client'} · ${m.orderCode || ''}` })),
    [missions]
  );

  const threads = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (!map.has(m.threadKey)) map.set(m.threadKey, { key: m.threadKey, people: new Map(), last: m.createdAt });
      const thread = map.get(m.threadKey);
      [m.from, m.to].forEach((p) => {
        if (p && p.role !== 'translator') thread.people.set(p.id, p);
      });
      if (new Date(m.createdAt) > new Date(thread.last || 0)) thread.last = m.createdAt;
    });
    return Array.from(map.values()).sort((x, y) => new Date(y.last || 0) - new Date(x.last || 0));
  }, [messages]);

  const threadLabel = (thread) => {
    const names = Array.from(thread.people.values()).map((p) => (p.role === 'admin' ? (tr.administration || 'Administration') : p.name)).filter(Boolean);
    return names.length ? names.join(' ↔ ') : thread.key;
  };

  const reload = () => translatorApi.messages().then((res) => setMessages(res.messages || [])).catch(() => setMessages([]));
  useEffect(() => {
    reload();
    translatorApi.missions().then((res) => setMissions(res.missions || [])).catch(() => setMissions([]));
  }, []);

  const pickThread = (key) => {
    setThreadKey(key);
    if (key?.startsWith('order:')) {
      const contact = contacts.find((x) => `order:${x.oid}` === key);
      setToSel(contact ? `${contact.cid}|${contact.oid}` : '');
    } else {
      setToSel('');
    }
  };

  const filtered = threadKey ? messages.filter((m) => m.threadKey === threadKey) : messages;
  const msgsPg = usePagination(filtered, 6);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      setMsg(t.auth?.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const [cid, oid] = toSel ? toSel.split('|') : [null, null];
      await translatorApi.sendMessage({ body, ...(cid ? { toId: cid, orderId: oid, threadKey: `order:${oid}` } : {}) });
      setBody('');
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || d.empty);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="dash-page-head"><h1>{tr.messagesTitle || 'Messages'}</h1><p>{tr.messagesSubtitle || 'Échangez avec l\'administrateur.'}</p></header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        {threads.length > 1 && (
          <div className="dash-field" style={{ marginBottom: 12 }}>
            <label>{t.dashboard.admin?.thread || 'Fil'}</label>
            <select value={threadKey} onChange={(e) => pickThread(e.target.value)}>
              <option value="">{t.dashboard.admin?.allThreads || 'Tous les fils'}</option>
              {threads.map((thread) => <option key={thread.key} value={thread.key}>{threadLabel(thread)}</option>)}
            </select>
          </div>
        )}
        <div className="dash-chat">
          {filtered.length === 0 ? <p className="dash-empty">{d.empty}</p> : (
            msgsPg.pageItems.map((m) => (
              <div key={m.id} className={`dash-chat-bubble${m.from?.role === 'translator' ? ' is-mine' : ''}`}>
                <strong>{m.from?.name || '—'}</strong><p>{m.body}</p><small>{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</small>
              </div>
            ))
          )}
        </div>
        <Pagination page={msgsPg.page} totalPages={msgsPg.totalPages} total={msgsPg.total} onChange={msgsPg.setPage} />
        <form className="dash-form" onSubmit={send} noValidate style={{ marginTop: 16, maxWidth: '100%' }}>
          {contacts.length > 0 && (
            <div className="dash-field">
              <label>{tr.recipient || 'Destinataire'}</label>
              <select value={toSel} onChange={(e) => setToSel(e.target.value)}>
                <option value="">{tr.administration || 'Administration'}</option>
                {contacts.map((x) => <option key={`${x.cid}|${x.oid}`} value={`${x.cid}|${x.oid}`}>{x.label}</option>)}
              </select>
            </div>
          )}
          <div className="dash-field"><label>{tr.messageLabel || 'Message'}</label><textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} required /></div>
          <button type="submit" className="btn btn-primary" disabled={busy}>{tr.sendMessage || 'Envoyer'}</button>
        </form>
      </section>
    </>
  );
}
